import { Card } from '../../ui';
import { BatchStatusBadge } from '../Batches/BatchStatusBadge';
import { formatNumber } from '../shared';
import type { BatchRead } from '../../../types/clustering';
import { userMessage } from '../../../utils/userMessage';
import styles from './LiveProgress.module.css';

interface LiveProgressProps {
  batch: BatchRead;
}

/** Ordered list of pipeline steps surfaced to the user. */
const STEPS: { key: string; label: string }[] = [
  { key: 'serp_fetch', label: 'Fetch SERPs' },
  { key: 'enrichment', label: 'Enrich metrics' },
  { key: 'clustering', label: 'Cluster' },
  { key: 'scoring', label: 'Score' },
  { key: 'labeling', label: 'Label' },
];

function stepIndex(current: string | null): number {
  if (!current) return -1;
  const i = STEPS.findIndex((s) => current.toLowerCase().includes(s.key));
  return i;
}

export function LiveProgress({ batch }: LiveProgressProps) {
  const pct = Math.max(0, Math.min(100, batch.progress_pct));
  const active = stepIndex(batch.current_step);
  const isDone = batch.status === 'done';
  const isFailed = batch.status === 'failed';
  const cacheTotal = batch.serp_cache_hits + batch.serp_cache_misses;
  const cachePct = cacheTotal ? Math.round((batch.serp_cache_hits / cacheTotal) * 100) : 0;

  return (
    <Card variant="outlined" padding="md">
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Progress</h3>
          <div className={styles.subtitle}>
            {batch.current_step
              ? `Current step: ${batch.current_step}`
              : 'Waiting for first event…'}
          </div>
        </div>
        <BatchStatusBadge status={batch.status} size="md" />
      </div>

      <div className={styles.progressBar} aria-label="Progress" role="progressbar" aria-valuenow={pct}>
        <div
          className={`${styles.progressFill} ${isFailed ? styles.progressFailed : ''} ${isDone ? styles.progressDone : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className={styles.progressLabel}>{pct.toFixed(0)}%</div>

      <ol className={styles.steps}>
        {STEPS.map((s, i) => {
          const state =
            isDone || i < active
              ? 'done'
              : i === active
                ? 'active'
                : 'pending';
          return (
            <li key={s.key} className={`${styles.step} ${styles[state]}`}>
              <span className={styles.stepDot} aria-hidden="true" />
              <span className={styles.stepLabel}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      <dl className={styles.stats}>
        <div>
          <dt>Keywords</dt>
          <dd>{formatNumber(batch.total_keywords)}</dd>
        </div>
        <div>
          <dt>Clusters</dt>
          <dd>{formatNumber(batch.clusters_count)}</dd>
        </div>
        <div>
          <dt>SERP cache hits</dt>
          <dd>
            {formatNumber(batch.serp_cache_hits)}
            {cacheTotal > 0 && (
              <span className={styles.statSuffix}> ({cachePct}%)</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Cache misses</dt>
          <dd>{formatNumber(batch.serp_cache_misses)}</dd>
        </div>
      </dl>

      {batch.error_message && (
        <div className={styles.errorBox} role="alert">
          {userMessage(batch.error_message, 'This batch could not be completed. Please try again.')}
        </div>
      )}
    </Card>
  );
}
